import { ActivityEntityName } from '../../services/apollo/inopack-graphql-schema';
import { Label, LabelMap, defineLabels } from './types';

/**
 * Per-entity overrides for columns whose name is shared but whose MEANING is
 * not. `account_id` is the customer on a sale and the supplier on an expense —
 * the same key, two different words. A single flat map would have to pick one
 * and be wrong in the other place, which for an audit log is worse than an
 * unlabelled field: it is confidently mislabelled.
 *
 * Only add a key here when the generic label would be actively WRONG, not
 * merely generic. That rule is what keeps this map small enough to read; the
 * moment it becomes a place to put any label that felt too broad, the shared map
 * stops being the source of truth and the drift starts again.
 *
 * Each entity's map is its own exported const rather than an inline object
 * literal, so that forms can index it by literal key and get a compile error
 * for a column nobody has named — the same guarantee `FIELD_LABELS` gives.
 * `ENTITY_FIELD_LABELS` below is the runtime index the audit reads.
 */
export const ORDER_SALE_FIELD_LABELS = defineLabels({
    account_id: { long: 'Cliente' },
    // A sale is dated by the document it issues, not by when it was captured.
    date: { long: 'Fecha de la factura/nota' },
    // We COLLECT on this date. The expense's twin means we PAY — same column,
    // opposite direction, which is exactly the test for living in this map.
    expected_payment_date: {
        short: 'Próxima cobranza',
        long: 'Fecha de la próxima cobranza',
    },
});

export const ORDER_REQUEST_FIELD_LABELS = defineLabels({
    account_id: { long: 'Cliente' },
    date: { long: 'Fecha de emisión' },
});

export const EXPENSE_FIELD_LABELS = defineLabels({
    account_id: { long: 'Proveedor' },
    date: { long: 'Fecha en que se realizó' },
    expected_payment_date: { long: 'Fecha del pago' },
});

export const ENTITY_FIELD_LABELS: Record<string, LabelMap> = {
    [ActivityEntityName.OrderSale]: ORDER_SALE_FIELD_LABELS,
    [ActivityEntityName.OrderRequest]: ORDER_REQUEST_FIELD_LABELS,
    [ActivityEntityName.Expense]: EXPENSE_FIELD_LABELS,
};

/**
 * Dynamic lookup — see the note on `lookupFieldLabel`. Returns `undefined` both
 * when the entity has no overrides at all and when it has some but not for this
 * column, which are the same thing to every caller: fall back to the shared map.
 */
export function lookupEntityFieldLabel(
    entityName: ActivityEntityName | null | undefined,
    column: string,
): Label | undefined {
    if (!entityName) return undefined;
    return ENTITY_FIELD_LABELS[entityName]?.[column];
}
